import React, { lazy, Suspense, useContext, createContext, useMemo, useRef, useEffect } from 'react';
import { supabase, db } from '../../lib/supabase';
import { getUserRole, isTeamManager } from '../../lib/roleUtils';
import { formatReservationDetailsForEmail, copyToClipboard } from '../../utils/reservationUtils';
import { smartFormatTime } from '../../utils/enhancedTimezoneUtils';
import { log } from '../../utils/logger';

// Create context for shared dependencies
// eslint-disable-next-line react-refresh/only-export-components
export const RouterContext = createContext({});

// Custom hook to access router context
// eslint-disable-next-line react-refresh/only-export-components
export const useRouterContext = () => {
  const context = useContext(RouterContext);
  if (!context) {
    throw new Error('useRouterContext must be used within RouterContext.Provider');
  }
  return context;
};

// Loading component
const LoadingSpinner = () => (
  <div className="flex items-center justify-center min-h-[400px]">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-navy-600"></div>
  </div>
);

// Error boundary for route components
class RouteErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    log.error('Route Error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="p-6 text-center">
          <h2 className="text-xl font-semibold text-red-600 mb-2">Something went wrong</h2>
          <p className="text-gray-600">{this.state.error?.message || 'Failed to load this page'}</p>
          <button
            onClick={() => this.setState({ hasError: false, error: null })}
            className="mt-4 px-4 py-2 bg-navy-600 text-white rounded-lg hover:bg-navy-700"
          >
            Try Again
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

// Route configuration
const routeConfig = {
  // Core routes available to all roles
  overview: {
    component: lazy(() => import('../pages/DashboardPages/OverviewPage')),
    requiredProps: ['currentUser', 'reservations', 'venues', 'setActiveTab'],
    roles: ['teamrezy_employee', 'team_manager', 'team_manager_primary', 'team_manager_assistant', 'venue_manager']
  },

  account: {
    component: lazy(() => import('./AccountContent')),
    requiredProps: ['currentUser', 'handleAccountUpdateSuccess'],
    roles: ['teamrezy_employee', 'team_manager', 'team_manager_primary', 'team_manager_assistant', 'venue_manager']
  },

  // Reservation routes
  reservations: {
    component: lazy(() => import('../pages/DashboardPages/ReservationsPage')),
    requiredProps: ['currentUser', 'reservations', 'venues', 'activeTab', 'setActiveTab'],
    actionProps: ['setReservations'],
    roles: ['teamrezy_employee', 'team_manager', 'team_manager_primary', 'team_manager_assistant', 'venue_manager']
  },

  book: {
    component: lazy(() => import('../pages/BookingPages/BookReservationPage')),
    requiredProps: ['currentUser', 'venues', 'setActiveTab', 'handleHotelSearch'],
    stateProps: ['preservedReservationState', 'setPreservedReservationState', 'reservationSuccess', 'setReservationSuccess'],
    actionProps: ['setReservations'],
    roles: ['team_manager', 'team_manager_primary', 'team_manager_assistant', 'teamrezy_employee']
  },

  // Venue management routes
  venues: {
    component: lazy(() => import('../pages/DashboardPages/VenuesPage')),
    requiredProps: ['currentUser', 'venues'],
    roles: ['teamrezy_employee', 'venue_manager']
  },

  venue: {
    component: lazy(() => import('../venues/VenueEditForm')),
    requiredProps: ['currentUser'],
    roles: ['venue_manager'],
    placeholder: 'Venue Settings - To be extracted'
  },

  // Team and tournament routes
  teams: {
    component: lazy(() => import('../pages/ManagementPages/TeamManagement')),
    requiredProps: [],
    roles: ['teamrezy_employee', 'team_manager', 'team_manager_primary', 'team_manager_assistant']
  },

  tournaments: {
    component: lazy(() => import('../tournaments/TeamManagerTournaments')),
    requiredProps: ['currentUser'],
    roles: ['team_manager', 'team_manager_primary', 'team_manager_assistant'],
    fallbackComponent: () => <div>Tournaments Content - To be extracted</div>,
    roleCheck: (user) => isTeamManager(user)
  },

  // Game management routes
  games: {
    component: lazy(() => import('../admin/GameManagement')),
    requiredProps: ['currentUser', 'setActiveTab'],
    roles: ['teamrezy_employee']
  },

  game_locations: {
    component: lazy(() => import('../admin/GameLocationManagement')),
    requiredProps: [],
    roles: ['teamrezy_employee']
  },

  tournament_games: {
    component: lazy(() => import('../pages/DashboardPages/TournamentGamesPage')),
    requiredProps: ['currentUser', 'setActiveTab'],
    roles: ['teamrezy_employee']
  },

  // User management routes
  users: {
    component: lazy(() => import('../admin/UserManagement')),
    requiredProps: [],
    roles: ['teamrezy_employee']
  },

  // Moved to Team Management page
  // pendingTeamManagers: {
  //   component: lazy(() => import('../PendingTeamManagersManagement')),
  //   requiredProps: ['currentUser'],
  //   roles: ['teamrezy_employee', 'team_manager']
  // },

  // Tool routes (mostly for employees)
  authDiagnostic: {
    component: lazy(() => import('../admin/AuthDiagnostic')),
    requiredProps: [],
    roles: ['teamrezy_employee', 'team_manager', 'team_manager_primary', 'team_manager_assistant']
  },

  hockeyFinderImport: {
    component: lazy(() => import('../admin/HockeyFinderImport')),
    requiredProps: ['currentUser'],
    roles: ['teamrezy_employee']
  },

  scheduleUpload: {
    component: lazy(() => import('../admin/ScheduleUpload/ScheduleUploadWizard')),
    requiredProps: ['currentUser'],
    roles: ['teamrezy_employee']
  },

  teamsImport: {
    component: lazy(() => import('../admin/TeamsImport/TeamsImportWizard')),
    requiredProps: ['currentUser'],
    roles: ['teamrezy_employee']
  },

  // Payment Admin routes (TeamRezy employees only)
  adminPanel: {
    component: lazy(() => import('../admin/AdminPanel')),
    requiredProps: ['currentUser', 'setActiveTab'],
    roles: ['teamrezy_employee']
  },

  paymentDashboard: {
    component: lazy(() => import('../admin/PaymentDashboard')),
    requiredProps: ['currentUser'],
    roles: ['teamrezy_employee']
  },

  paymentAnalytics: {
    component: lazy(() => import('../admin/PaymentAnalytics')),
    requiredProps: ['currentUser'],
    roles: ['teamrezy_employee']
  },

  paymentExport: {
    component: lazy(() => import('../admin/PaymentExport')),
    requiredProps: ['currentUser'],
    roles: ['teamrezy_employee']
  },

  bulkRefundManager: {
    component: lazy(() => import('../admin/BulkRefundManager')),
    requiredProps: ['currentUser'],
    roles: ['teamrezy_employee']
  },

  bulkInviteCodes: {
    component: lazy(() => import('../admin/BulkInviteCodeGeneration')),
    requiredProps: ['currentUser'],
    roles: ['teamrezy_employee']
  },

  // Contact Us page
  contact: {
    component: lazy(() => import('../pages/PublicPages/ContactUsPage')),
    requiredProps: ['currentUser'],
    roles: ['teamrezy_employee', 'team_manager', 'team_manager_primary', 'team_manager_assistant', 'venue_manager']
  },

  // Notifications page
  notifications: {
    component: lazy(() => import('../pages/DashboardPages/NotificationsPage')),
    requiredProps: ['currentUser'],
    roles: ['teamrezy_employee', 'team_manager', 'team_manager_primary', 'team_manager_assistant', 'venue_manager']
  },

  // Data deletion requests management (TeamRezy employees only)
  deletionRequests: {
    component: lazy(() => import('../pages/AdminPages/DeletionRequestsPage')),
    requiredProps: ['currentUser'],
    roles: ['teamrezy_employee']
  }

};

// Helper to filter props based on route configuration
const filterRouteProps = (allProps, route) => {
  const filteredProps = {};

  // Add required props
  (route.requiredProps || []).forEach(prop => {
    if (prop in allProps) {
      filteredProps[prop] = allProps[prop];
    }
  });

  // Add action props (from actions object)
  (route.actionProps || []).forEach(prop => {
    if (allProps.actions && prop in allProps.actions) {
      filteredProps[prop] = allProps.actions[prop];
    }
  });

  // Add state props
  (route.stateProps || []).forEach(prop => {
    if (prop in allProps) {
      filteredProps[prop] = allProps[prop];
    }
  });

  // Always add shared utilities from context
  filteredProps.db = allProps.db || db;
  filteredProps.supabase = allProps.supabase || supabase;
  filteredProps.formatReservationDetailsForEmail = allProps.formatReservationDetailsForEmail || formatReservationDetailsForEmail;
  filteredProps.copyToClipboard = allProps.copyToClipboard || copyToClipboard;
  filteredProps.smartFormatTime = allProps.smartFormatTime || smartFormatTime;

  return filteredProps;
};

// Check if user has access to route
const hasRouteAccess = (route, currentUser) => {
  if (!route.roles || route.roles.length === 0) {
    return true; // No role restriction
  }

  const userRole = currentUser?.role || getUserRole(currentUser);
  return route.roles.includes(userRole);
};

// Special hotel search component handler
const HotelSearchHandler = ({ hotelSearchData, setShowHotelSearch, setHotelSearchData, setActiveTab, preservedReservationState }) => {
  const HotelSearchComponent = lazy(() => import('../hotels/HotelSearchComponent'));

  return (
    <Suspense fallback={<LoadingSpinner />}>
      <HotelSearchComponent
        tournament={hotelSearchData.tournament}
        team={hotelSearchData.team}
        existingHotel={hotelSearchData.existingHotel}
        source={hotelSearchData.source}
        onHotelSelect={async (hotelData) => {
          log.info('🏨 Hotel selected, saving to database:', hotelData);

          try {
            const hotelRecord = {
              team_id: hotelSearchData.team.id,
              tournament_id: hotelSearchData.tournament.id,
              hotel_name: hotelData.hotel_name,
              hotel_address: hotelData.hotel_address,
              latitude: hotelData.latitude,
              longitude: hotelData.longitude,
              place_id: hotelData.place_id,
              phone_number: hotelData.phone_number || null,
              website_url: hotelData.website_url || hotelData.website || null,
              rating: hotelData.rating || null,
              price_level: hotelData.price_level || null
            };

            let result;
            if (hotelSearchData.existingHotel) {
              result = await db.updateTeamTournamentHotel(hotelSearchData.existingHotel.id, hotelRecord);
            } else {
              result = await db.createTeamTournamentHotel(hotelRecord);
            }

            const { data, error } = result;

            if (error) {
              log.error('❌ Error saving hotel:', error);
              return { success: false, error: error.message || 'Failed to save hotel accommodation. Please try again.' };
            }

            if (hotelSearchData?.team?.id) {
              window.dispatchEvent(new CustomEvent('hotelSaved', {
                detail: {
                  teamId: hotelSearchData.team.id,
                  tournamentId: hotelSearchData.tournament.id
                }
              }));
            }

            return {
              success: true,
              hotel: data,
              message: `Hotel accommodation saved successfully!`
            };

          } catch (error) {
            log.error('❌ Error saving hotel accommodation:', error);
            return { success: false, error: 'An error occurred while saving the hotel. Please try again.' };
          }
        }}
        onCancel={() => {
          if (hotelSearchData.source === 'reservations' && preservedReservationState) {
            setActiveTab('book');
          }
          setShowHotelSearch(false);
          setHotelSearchData(null);
        }}
        onBack={() => {
          if (hotelSearchData.source === 'reservations') {
            setActiveTab('book');
          }
          setShowHotelSearch(false);
          setHotelSearchData(null);
        }}
      />
    </Suspense>
  );
};

// Overview component import for fallback
const OverviewComponent = lazy(() => import('../pages/DashboardPages/OverviewPage'));

// Main ContentRouter component
const ContentRouter = (props) => {
  const {
    currentUser,
    activeTab = 'overview',
    showHotelSearch,
    hotelSearchData,
    setShowHotelSearch,
    setHotelSearchData,
    setActiveTab,
    preservedReservationState
  } = props;

  // Log meaningful changes only, not on every render
  const userRole = currentUser?.role || getUserRole(currentUser);
  useEffect(() => {
    log.info('ContentRouter: activeTab =', activeTab, 'currentUser role =', userRole);
  }, [activeTab, userRole]); // Only log when these actually change

  // Use ref to track dashboardData changes without causing re-renders
  const dashboardDataRef = useRef(props.dashboardData);

  // Stabilize context value to prevent unnecessary re-renders
  // Use ref to access dashboard data without causing re-renders
  const contextValue = useMemo(() => ({
    db: db,
    supabase: supabase,
    formatReservationDetailsForEmail: formatReservationDetailsForEmail,
    copyToClipboard: copyToClipboard,
    smartFormatTime: smartFormatTime,
    isTeamManager: isTeamManager,
    getUserRole,
    setActiveTab,
    // Include dashboard data getter that accesses ref directly
    get dashboardData() {
      return {
        data: dashboardDataRef.current?.data || {},
        loading: dashboardDataRef.current?.loading || {},
        errors: dashboardDataRef.current?.errors || {},
        actions: dashboardDataRef.current?.actions || {},
        selectors: dashboardDataRef.current?.selectors || {},
        isReady: dashboardDataRef.current?.isReady || false,
        hasData: dashboardDataRef.current?.hasData || false,
      };
    }
  }), [setActiveTab]); // Include setActiveTab in dependencies

  // Pre-calculate route before any conditional logic
  const route = routeConfig[activeTab];
  
  // Memoize filtered props more aggressively to prevent re-renders
  const overviewProps = useMemo(() => filterRouteProps(props, routeConfig.overview), [props]);
  const filteredProps = useMemo(() => {
    if (!route) return {};
    return filterRouteProps(props, route);
  }, [props, route]); // Use route instead of activeTab to avoid circular dependency
  
  // Pre-memoize the component if it exists
  const Component = route?.component;
  const memoizedComponent = useMemo(() => {
    if (!Component) return null;
    return <Component {...filteredProps} />;
  }, [Component, filteredProps]);

  // Handle hotel search override
  if (showHotelSearch && hotelSearchData) {
    return (
      <RouterContext.Provider value={contextValue}>
        <HotelSearchHandler
          hotelSearchData={hotelSearchData}
          setShowHotelSearch={setShowHotelSearch}
          setHotelSearchData={setHotelSearchData}
          setActiveTab={setActiveTab}
          preservedReservationState={preservedReservationState}
        />
      </RouterContext.Provider>
    );
  }

  // Default to overview if route not found
  if (!route) {
    log.warn(`Route "${activeTab}" not found in routeConfig. Available routes:`, Object.keys(routeConfig));

    return (
      <RouterContext.Provider value={contextValue}>
        <RouteErrorBoundary>
          <Suspense fallback={<LoadingSpinner />}>
            <OverviewComponent {...overviewProps} />
          </Suspense>
        </RouteErrorBoundary>
      </RouterContext.Provider>
    );
  }

  // Check role-based access (remove frequent logging)
  if (!hasRouteAccess(route, currentUser)) {
    log.warn(`User does not have access to route "${activeTab}". Using overview as fallback.`);

    // Use fallback component if specified, otherwise overview
    if (route.fallbackComponent) {
      const FallbackComponent = route.fallbackComponent;
      return (
        <RouterContext.Provider value={contextValue}>
          <FallbackComponent />
        </RouterContext.Provider>
      );
    }

    return (
      <RouterContext.Provider value={contextValue}>
        <RouteErrorBoundary>
          <Suspense fallback={<LoadingSpinner />}>
            <OverviewComponent {...overviewProps} />
          </Suspense>
        </RouteErrorBoundary>
      </RouterContext.Provider>
    );
  }

  // Special handling for tournaments
  if (activeTab === 'tournaments' && route.roleCheck && !route.roleCheck(currentUser)) {
    const FallbackComponent = route.fallbackComponent || (() => <div>Tournaments Content - To be extracted</div>);
    return (
      <RouterContext.Provider value={contextValue}>
        <FallbackComponent />
      </RouterContext.Provider>
    );
  }

  // Handle placeholder routes
  if (route.placeholder && !route.component) {
    return (
      <RouterContext.Provider value={contextValue}>
        <div className="p-6 text-center text-gray-500">{route.placeholder}</div>
      </RouterContext.Provider>
    );
  }

  // Special handling for full-screen components like AdminPanel
  if (activeTab === 'adminPanel') {
    const Component = route.component;
    return (
      <RouterContext.Provider value={contextValue}>
        <RouteErrorBoundary>
          <Suspense fallback={<LoadingSpinner />}>
            <div className="fixed inset-0 z-50 bg-white">
              <Component {...filteredProps} />
            </div>
          </Suspense>
        </RouteErrorBoundary>
      </RouterContext.Provider>
    );
  }

  // Render the component normally
  return (
    <RouterContext.Provider value={contextValue}>
      <RouteErrorBoundary>
        <Suspense fallback={<LoadingSpinner />}>
          {memoizedComponent || <Component {...filteredProps} />}
        </Suspense>
      </RouteErrorBoundary>
    </RouterContext.Provider>
  );
};

export default ContentRouter;
